<template>
  <auth-layout>
    <plan-summary-card
      v-if="selected"
      :plan="selected"
      :code="promoCode"
      :coupon="coupon"
      :affiliate-only="affiliateOnly"
      :error="couponError"
      :busy="couponBusy"
      @update:code="promoCode = $event"
      @apply="applyCoupon"
      @change-plan="changePlan"
    />

    <h1 class="pop-auth__h1">Create your POP account</h1>
    <p class="pop-auth__sub">Optimize your first page in minutes. No credit card required.</p>

    <div v-if="expiredNotice" class="pop-auth__box pop-auth__box--info" style="margin-bottom: 18px">
      Your previous signup expired. Start again below.
    </div>

    <social-buttons :busy="socialBusy" @google="onGoogle" @apple="onApple" />

    <form novalidate @submit.prevent="submit">
      <label class="pop-auth__label" for="signup-email">Email</label>
      <input
        id="signup-email"
        v-model.trim="email"
        class="pop-auth__field"
        :class="{ 'is-invalid': emailError || exists }"
        type="email"
        placeholder="you@company.com"
        autocomplete="email"
        data-test="signup-email"
        @blur="checkEmail"
        @input="exists = null; emailServerError = ''"
      />
      <div v-if="emailError" class="pop-auth__err">{{ emailError }}</div>
      <div v-if="exists && exists.subAccount" class="pop-auth__box pop-auth__box--info" data-test="signup-exists">
        This email already has a POP sub-account.
        <router-link class="pop-auth__link" :to="{ path: '/login', query: { email } }">Log in</router-link> with it,
        or use a different email to get your own subscription.
      </div>
      <div v-else-if="exists" class="pop-auth__box pop-auth__box--info" data-test="signup-exists">
        An account with this email already exists<span v-if="!exists.hasPassword"> and uses {{ existsSocialLabel }} sign-in</span>.
        <router-link class="pop-auth__link" :to="{ path: '/login', query: { email } }">Log in</router-link><span v-if="exists.hasPassword"> or
        <router-link class="pop-auth__link" :to="{ path: '/forgotpassword', query: { email } }">reset your password</router-link></span>.
      </div>

      <label class="pop-auth__label" for="signup-password" style="margin-top: 20px">Password</label>
      <password-field
        v-model="password"
        input-id="signup-password"
        placeholder="Create a password"
        autocomplete="new-password"
        :error="passwordError"
        :meter="true"
        data-test="signup-password"
        @enter="submit"
      />

      <div v-if="submitError" class="pop-auth__box pop-auth__box--danger" data-test="signup-error">{{ submitError }}</div>

      <turnstile-widget ref="captcha" action="signup" @token="captchaToken = $event" @expired="captchaToken = ''" />
      <button type="submit" class="pop-auth__btn pop-auth__btn--primary" :disabled="busy || captchaPending" data-test="signup-submit">
        {{ busy ? 'Creating your account' : 'Create account' }}
      </button>
    </form>

    <p class="pop-auth__fine pop-auth__center" style="margin-top: 20px">
      Already have an account? <router-link class="pop-auth__link" to="/login">Log in</router-link>
    </p>

    <template #footer><auth-terms-footer /></template>
  </auth-layout>
</template>

<script>
import { debounce } from 'lodash'
import AuthLayout from '../AuthLayout.vue'
import PasswordField from '../PasswordField.vue'
import PlanSummaryCard from '../PlanSummaryCard.vue'
import AuthTermsFooter from '../AuthTermsFooter.vue'
import TurnstileWidget from '../TurnstileWidget.vue'
import SocialButtons from '../SocialButtons.vue'
import { exchangeSocialCode, providerLabel } from '../social'
import { pickCouponForPlan } from '../coupons'
import { mapProductParam, selectedPlan } from '../plans'
import { isTurnstileEnabled } from '@/utils/turnstile'
import { getDeviceId } from '@/utils/device-id'
import { getToken, segmentPage, segmentTrack } from '@/utils'
import { DEMO_SESSION_KEYS } from '@/components/PageRun/constants'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const MIN_PASSWORD = 8

export default {
  name: 'SignupPage',
  components: { AuthLayout, PasswordField, PlanSummaryCard, AuthTermsFooter, SocialButtons, TurnstileWidget },
  data() {
    return {
      socialBusy: false,
      captchaToken: '',
      email: '',
      password: '',
      touched: false,
      busy: false,
      exists: null, // { hasPassword, providers }
      emailServerError: '',
      submitError: '',
      expiredNotice: false,
      promoCode: '',
      coupon: null,
      affiliateOnly: false,
      couponError: '',
      couponBusy: false,
    }
  },
  computed: {
    // "uses Google sign-in" / "uses Apple sign-in" / "uses Google or Apple sign-in" for a social-only account
    existsSocialLabel() {
      const social = ((this.exists && this.exists.providers) || []).filter((p) => p !== 'password')
      const labels = social.map((p) => providerLabel(p)).filter((v, i, a) => a.indexOf(v) === i)
      return labels.join(' or ') || 'Google'
    },
    signup() {
      return this.$store.state.signup
    },
    selected() {
      return this.signup.plan
    },
    captchaPending() {
      return isTurnstileEnabled() && !this.captchaToken
    },
    emailError() {
      if (this.emailServerError) return this.emailServerError
      if (!this.touched && !this.email) return ''
      if (this.email && !EMAIL_RE.test(this.email)) return 'Enter a valid email address.'
      if (this.touched && !this.email) return 'Enter your email address.'
      return ''
    },
    passwordError() {
      if (!this.touched) return ''
      return this.password.length < MIN_PASSWORD ? `Password needs at least ${MIN_PASSWORD} characters.` : ''
    },
  },
  created() {
    this.checkEmail = debounce(this.checkEmail, 300)
  },
  mounted() {
    Object.values(DEMO_SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key))
    if (getToken()) {
      // Already logged in (see LoginPage): never clear a live session from here. A pricing link
      // with a product goes to the plan picker, anything else to the dashboard.
      this.$router.replace(this.$route.query.product ? '/plans' : '/sites')
      return
    }
    this.$store.commit('clearStore')
    this.readQuery()
    segmentPage('Signup', { promoCode: this.promoCode, product: this.$route.query.product || '', entry: this.signup.entry })
    if (this.selected) this.viewItemForGtm()
  },
  methods: {
    readQuery() {
      const q = this.$route.query
      const patch = {}
      const affiliate = q.affiliateCode && q.affiliateCode !== 'null' ? String(q.affiliateCode) : ''
      const promo = String(q.promoCode || q.coupon || q.promo || '').trim()
      const mapped = q.product ? mapProductParam(q.product) : null
      if (mapped) patch.plan = selectedPlan(mapped.plan, mapped.interval)
      if (affiliate) patch.affiliateCode = affiliate
      if (promo) patch.promoCode = promo
      const utm = {}
      ;['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => { if (q[k]) utm[k.replace('utm_', '')] = String(q[k]) })
      if (Object.keys(utm).length) patch.utm = utm
      if (q.redirect) patch.redirect = String(q.redirect)
      if (q.email) this.email = String(q.email)
      this.expiredNotice = q.expired === '1'
      if (affiliate || mapped || promo) {
        patch.entry = affiliate ? 'affiliate' : mapped ? 'pricing' : 'campaign'
      } else if (!this.signup.plan) {
        patch.entry = 'direct' // coming back from /register/plans keeps the entry and the chosen plan
      }
      if (Object.keys(patch).length) this.$store.commit('signup/patch', patch)

      this.promoCode = this.signup.promoCode || this.signup.affiliateCode || ''
      this.coupon = this.signup.coupon
      if (this.promoCode && this.selected && !this.coupon) this.applyCoupon()
    },
    changePlan() {
      // The Select a plan screen, before the account exists; ?product= is dropped so the choice
      // made there is not overwritten by the pricing-page preselection on the way back.
      const query = { ...this.$route.query }
      delete query.product
      this.$router.push({ path: '/register/plans', query })
    },
    async applyCoupon() {
      const code = (this.promoCode || '').trim().toUpperCase()
      if (!code) return
      this.couponBusy = true
      this.couponError = ''
      try {
        const response = await this.$store.dispatch('fetchValidatedCoupons', { promoCode: code, isRegisterPage: 1 })
        const { coupon, affiliateOnly, error } = pickCouponForPlan(response.data, this.selected && this.selected.code)
        this.coupon = coupon
        this.affiliateOnly = affiliateOnly
        this.couponError = error
        this.promoCode = code
        this.$store.commit('signup/patch', { promoCode: code, coupon })
      } catch (e) {
        console.log(e)
        this.couponError = 'Could not check this code right now.'
      } finally {
        this.couponBusy = false
      }
    },
    async checkEmail() {
      const email = (this.email || '').trim()
      if (!email || !EMAIL_RE.test(email)) return
      try {
        const response = await this.$store.dispatch('emailIsAvailable', email)
        const data = response.data || {}
        if (data.action === 'ACCOUNT_EXISTS' || data.action === 'ACCOUNT_EXISTS_SUB_ACCOUNT') {
          this.exists = { hasPassword: data.hasPassword !== false, providers: data.providers || [], subAccount: data.action === 'ACCOUNT_EXISTS_SUB_ACCOUNT' }
        } else if (data.action === 'TEMP_EMAIL') {
          this.emailServerError = 'Temporary email addresses are not accepted. Use your work or personal email.'
        } else if (data.action === 'ACCOUNT_DELETED') {
          this.emailServerError = 'This email was removed from PageOptimizer Pro and cannot be used again.'
        }
      } catch (e) {
        console.log(e)
      }
    },
    viewItemForGtm() {
      try {
        const plan = this.selected
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: 'view_item',
          ecommerce: {
            value: plan.price,
            currency: 'USD',
            items: [{ item_id: plan.code, item_name: `POP ${plan.title}`,
              item_category: plan.interval === 'year' ? 'Yearly' : 'Monthly', price: plan.price, discount: 0 }],
          },
        })
      } catch (e) {
        console.log(e)
      }
    },
    onGoogle(code) {
      return this.social({ provider: 'google', code })
    },
    onApple({ code, name, nonce }) {
      return this.social({ provider: 'apple', code, name, nonce })
    },
    async social(credential) {
      this.socialBusy = true
      this.submitError = ''
      try {
        this.submitError = await exchangeSocialCode(this, { ...credential, intent: 'signup' })
      } catch (e) {
        console.log(e)
        this.submitError = `${providerLabel(credential.provider)} sign-in didn't complete. Please try again.`
      } finally {
        this.socialBusy = false
      }
    },
    async submit() {
      this.touched = true
      this.submitError = ''
      if (this.emailError || this.passwordError || this.exists || !this.email) return
      this.busy = true
      try {
        const captchaToken = this.captchaToken
        const response = await this.$store.dispatch('signupStart', {
          email: this.email,
          password: this.password,
          captchaToken,
          promoCode: this.signup.promoCode || this.signup.affiliateCode || '',
          servicePackage: this.selected ? this.selected.code : '',
          utm: this.signup.utm || null,
          deviceId: getDeviceId(),
        })
        this.handleStart(response.data || {})
      } catch (e) {
        console.log(e)
        this.submitError = 'Could not start the signup. Please try again.'
      } finally {
        this.busy = false
        if (this.$refs.captcha) this.$refs.captcha.reset() // tokens are single-use
      }
    },
    handleStart(data) {
      switch (data.action) {
        case 'VERIFY_EMAIL':
          this.$store.commit('signup/patch', {
            email: data.email || this.email.toLowerCase(),
            provider: 'password',
            pendingToken: data.pendingToken,
            resendAvailableAt: Date.now() + (data.resendAfterSeconds || 45) * 1000,
            lockedUntil: 0,
          })
          segmentTrack('signup_started', {
            email: data.email, method: 'password', entry: this.signup.entry,
            servicePackage: this.selected ? this.selected.code : '', promoCode: this.signup.promoCode,
            affiliateCode: this.signup.affiliateCode, utm_source: (this.signup.utm || {}).source,
            utm_medium: (this.signup.utm || {}).medium,
          })
          this.$router.push('/register/verify')
          break
        case 'ACCOUNT_EXISTS':
        case 'ACCOUNT_EXISTS_SUB_ACCOUNT':
          this.exists = { hasPassword: data.hasPassword !== false, providers: data.providers || [], subAccount: data.action === 'ACCOUNT_EXISTS_SUB_ACCOUNT' }
          break
        case 'ACCOUNT_DELETED':
        case 'TEMP_EMAIL':
        case 'INVALID_EMAIL':
          this.emailServerError = data.msg
          break
        case 'WEAK_PASSWORD':
          this.touched = true
          this.submitError = data.msg
          break
        case 'CODE_LOCKED':
          this.submitError = `Too many attempts. Try again in ${Math.ceil((data.retryAfterSeconds || 900) / 60)} minutes.`
          break
        case 'SIGNUP_DISABLED':
          this.submitError = 'Signup is not available right now. Please try again later.'
          break
        default:
          this.submitError = data.msg || "We couldn't create your account. Please try again."
      }
    },
  },
}
</script>
